import { Link } from "react-router-dom";
import React, { useState } from "react";
import { API_BASE } from "@/lib/api";
import UserBadge from "@/components/UserBadge";

export function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

export function Avatar({ user, size = 32 }) {
  const src = resolveImg(user?.avatar_url);
  const letter = (user?.username || "?").slice(0, 1).toUpperCase();
  if (src) {
    return <img src={src} alt={user?.username} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" />;
  }
  return (
    <div style={{ width: size, height: size }} className="rounded-full bg-gradient-to-br from-blue-600 to-blue-800 text-white flex items-center justify-center font-black shrink-0 text-xs">
      {letter}
    </div>
  );
}

export function StatCard({ label, value, Icon, color, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center ${color}`}>
          <Icon size={22} weight="bold" />
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
          <div className="text-2xl font-black text-slate-900 dark:text-white leading-tight" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function activityClass(active) {
  if (active) return "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300";
  return "bg-slate-100 text-slate-500";
}

function TreeItem({ node, level }) {
  const [open, setOpen] = useState(level < 2);
  const hasChildren = (node.children || []).length > 0;
  const activityCls = activityClass(node.is_active);
  return (
    <div className="border-l-2 border-slate-200 dark:border-slate-700 pl-3 mt-2">
      <div className="flex items-center gap-2 flex-wrap">
        {hasChildren ? (
          <button
            onClick={() => setOpen((o) => !o)}
            className="h-5 w-5 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-blue-100 dark:hover:bg-blue-900 flex items-center justify-center text-slate-700 dark:text-slate-200 text-xs font-black transition-colors duration-200"
            data-testid={`tree-toggle-${node.id}`}
          >
            {open ? "−" : "+"}
          </button>
        ) : (
          <span className="h-5 w-5 inline-block" />
        )}
        <Avatar user={node} size={28} />
        <Link to={`/u/${node.id}`} className="font-black text-slate-900 dark:text-white hover:underline inline-flex items-center gap-1">{node.username}<UserBadge user={node} size={12} /></Link>
        {node.membership && node.membership !== "Free" && (
          <span className="text-[10px] font-bold uppercase tracking-widest text-yellow-600">{node.membership}</span>
        )}
        <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full ${activityCls}`}>
          {node.is_active ? "active" : "inactive"}
        </span>
        <span className="text-[11px] text-slate-500 dark:text-slate-400">
          L{level + 1} • {node.descendants || 0} downline
        </span>
        {(node.total_ref_credits > 0 || node.total_ref_cash > 0) && (
          <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-bold">
            {node.total_ref_credits ? `+${node.total_ref_credits.toFixed(0)} cr ` : ""}
            {node.total_ref_cash ? `+$${node.total_ref_cash.toFixed(2)}` : ""}
          </span>
        )}
      </div>
      {open && hasChildren && (
        <div className="mt-1">
          {node.children.map((c) => React.createElement(TreeItem, { key: c.id, node: c, level: level + 1 }))}
        </div>
      )}
    </div>
  );
}

export function TreeNode(props) {
  return <TreeItem node={props.node} level={props.level ?? 0} />;
}
